/* ============================================
   POPNETIC ANIMATIONS & INTERACTIONS ENGINE
   Smooth scroll, intersection animations, micro-interactions
   ============================================ */

(function() {
  'use strict';

  // --- Intersection Observer for Scroll Animations ---
  const animationObserver = new IntersectionObserver(
    (entries) => {
      entries.forEach((entry) => {
        if (entry.isIntersecting) {
          entry.target.classList.add('pop-visible');
          // Unobserve after animation triggers (one-time)
          if (!entry.target.hasAttribute('data-pop-repeat')) {
            animationObserver.unobserve(entry.target);
          }
        } else if (entry.target.hasAttribute('data-pop-repeat')) {
          entry.target.classList.remove('pop-visible');
        }
      });
    },
    {
      threshold: 0.15,
      rootMargin: '0px 0px -50px 0px'
    }
  );

  function initScrollAnimations() {
    const animatedElements = document.querySelectorAll('[data-pop-animate], [data-pop-stagger]');
    animatedElements.forEach((el) => animationObserver.observe(el));
  }

  // --- Smooth Scroll (Lenis-like native implementation) ---
  let smoothScrollEnabled = false;

  function initSmoothScroll() {
    // Only enable on non-touch devices with no reduced motion preference
    if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) return;
    if ('ontouchstart' in window && window.innerWidth < 1024) return;

    smoothScrollEnabled = true;
    document.documentElement.style.scrollBehavior = 'smooth';
  }

  // --- Magnetic Buttons (micro-interaction) ---
  function initMagneticButtons() {
    const buttons = document.querySelectorAll('[data-pop-magnetic]');

    buttons.forEach((btn) => {
      btn.addEventListener('mousemove', (e) => {
        const rect = btn.getBoundingClientRect();
        const x = e.clientX - rect.left - rect.width / 2;
        const y = e.clientY - rect.top - rect.height / 2;
        const strength = btn.dataset.popMagnetic || 0.3;

        btn.style.transform = `translate(${x * strength}px, ${y * strength}px)`;
      });

      btn.addEventListener('mouseleave', () => {
        btn.style.transform = 'translate(0, 0)';
        btn.style.transition = 'transform 0.4s cubic-bezier(0.16, 1, 0.3, 1)';
      });

      btn.addEventListener('mouseenter', () => {
        btn.style.transition = 'none';
      });
    });
  }

  // --- Parallax Elements ---
  function initParallax() {
    const parallaxElements = document.querySelectorAll('[data-pop-parallax]');
    if (!parallaxElements.length) return;
    if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) return;

    let ticking = false;

    function updateParallax() {
      const scrollY = window.scrollY;

      parallaxElements.forEach((el) => {
        const speed = parseFloat(el.dataset.popParallax) || 0.3;
        const rect = el.getBoundingClientRect();
        const elementTop = rect.top + scrollY;
        const offset = (scrollY - elementTop) * speed;

        if (rect.top < window.innerHeight && rect.bottom > 0) {
          el.style.transform = `translateY(${offset}px)`;
        }
      });

      ticking = false;
    }

    window.addEventListener('scroll', () => {
      if (!ticking) {
        requestAnimationFrame(updateParallax);
        ticking = true;
      }
    }, { passive: true });
  }

  // --- Text Split Animation (for hero text) ---
  function initTextSplit() {
    const splitElements = document.querySelectorAll('[data-pop-split]');

    splitElements.forEach((el) => {
      const text = el.textContent;
      const type = el.dataset.popSplit || 'chars'; // 'chars' or 'words'
      let items;

      if (type === 'chars') {
        items = text.split('');
      } else {
        items = text.split(' ');
      }

      el.innerHTML = items
        .map((item, i) => {
          const display = type === 'words' ? item + ' ' : item;
          return `<span class="pop-split-item" style="--pop-split-index: ${i}; animation-delay: ${i * 40}ms">${display === ' ' ? '&nbsp;' : display}</span>`;
        })
        .join('');

      el.classList.add('pop-split-ready');
    });
  }

  // --- Cursor Follower (desktop only) ---
  function initCursorFollower() {
    if ('ontouchstart' in window) return;
    if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) return;

    const cursor = document.createElement('div');
    cursor.className = 'pop-cursor';
    cursor.innerHTML = '<div class="pop-cursor__dot"></div>';
    document.body.appendChild(cursor);

    let mouseX = 0, mouseY = 0;
    let cursorX = 0, cursorY = 0;

    document.addEventListener('mousemove', (e) => {
      mouseX = e.clientX;
      mouseY = e.clientY;
    });

    function animateCursor() {
      cursorX += (mouseX - cursorX) * 0.15;
      cursorY += (mouseY - cursorY) * 0.15;
      cursor.style.transform = `translate(${cursorX}px, ${cursorY}px)`;
      requestAnimationFrame(animateCursor);
    }

    animateCursor();

    // Scale cursor on interactive elements
    const interactiveElements = document.querySelectorAll('a, button, [data-pop-magnetic], .pop-card');
    interactiveElements.forEach((el) => {
      el.addEventListener('mouseenter', () => cursor.classList.add('pop-cursor--active'));
      el.addEventListener('mouseleave', () => cursor.classList.remove('pop-cursor--active'));
    });
  }

  // --- Counter Animation ---
  function initCounters() {
    const counters = document.querySelectorAll('[data-pop-counter]');

    const counterObserver = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            const target = parseInt(entry.target.dataset.popCounter, 10);
            const duration = parseInt(entry.target.dataset.popCounterDuration, 10) || 2000;
            animateCounter(entry.target, 0, target, duration);
            counterObserver.unobserve(entry.target);
          }
        });
      },
      { threshold: 0.5 }
    );

    counters.forEach((el) => counterObserver.observe(el));
  }

  function animateCounter(element, start, end, duration) {
    const startTime = performance.now();

    function update(currentTime) {
      const elapsed = currentTime - startTime;
      const progress = Math.min(elapsed / duration, 1);
      // Ease out quad
      const eased = 1 - (1 - progress) * (1 - progress);
      const current = Math.round(start + (end - start) * eased);

      element.textContent = current.toLocaleString('en-IN');

      if (progress < 1) {
        requestAnimationFrame(update);
      }
    }

    requestAnimationFrame(update);
  }

  // --- Tilt Effect on Cards ---
  function initTiltCards() {
    const cards = document.querySelectorAll('[data-pop-tilt]');
    if ('ontouchstart' in window) return;

    cards.forEach((card) => {
      card.addEventListener('mousemove', (e) => {
        const rect = card.getBoundingClientRect();
        const x = (e.clientX - rect.left) / rect.width;
        const y = (e.clientY - rect.top) / rect.height;
        const tiltX = (0.5 - y) * 10;
        const tiltY = (x - 0.5) * 10;

        card.style.transform = `perspective(1000px) rotateX(${tiltX}deg) rotateY(${tiltY}deg) scale3d(1.02, 1.02, 1.02)`;
      });

      card.addEventListener('mouseleave', () => {
        card.style.transform = 'perspective(1000px) rotateX(0) rotateY(0) scale3d(1, 1, 1)';
        card.style.transition = 'transform 0.5s cubic-bezier(0.16, 1, 0.3, 1)';
      });

      card.addEventListener('mouseenter', () => {
        card.style.transition = 'none';
      });
    });
  }

  // --- Image Reveal on Scroll ---
  function initImageReveal() {
    const images = document.querySelectorAll('[data-pop-reveal]');

    const revealObserver = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            entry.target.classList.add('pop-revealed');
            revealObserver.unobserve(entry.target);
          }
        });
      },
      { threshold: 0.2 }
    );

    images.forEach((img) => revealObserver.observe(img));
  }

  // --- Initialize Everything ---
  function init() {
    initScrollAnimations();
    initSmoothScroll();
    initMagneticButtons();
    initParallax();
    initTextSplit();
    initCounters();
    initTiltCards();
    initImageReveal();

    // Cursor follower only on larger screens
    if (window.innerWidth > 1024) {
      initCursorFollower();
    }
  }

  // Run on DOM ready
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }

  // Re-init on Shopify section reload (Theme Editor)
  document.addEventListener('shopify:section:load', () => {
    initScrollAnimations();
    initMagneticButtons();
    initParallax();
    initTiltCards();
    initImageReveal();
  });

})();
